import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Plus, Trash2, Edit2, ChevronLeft, ChevronRight, Repeat, Calendar as CalendarSolo } from 'lucide-react';
import {
  API, PLATFORMS, STATUSES, STATUS_COLORS, THEME_COLORS, THEME_COLOR_CLASS,
  PLATFORM_SHORT, PLATFORM_COLORS, startOfWeek, fmt,
} from './constants';
import { ConfirmDialog } from './ConfirmDialog';
import { MediaManager } from './MediaManager';

export const PlannerPanel = () => {
  const { getAuthHeaders } = useAuth();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [plans, setPlans] = useState([]);
  const [themesByDate, setThemesByDate] = useState({}); // dateStr -> {name, color, source}

  // Plan dialog (create/edit a single planned post)
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    date: '', theme: '', post_idea: '', platform: '', notes: '', status: 'Idea',
  });
  const [pendingPlanDeleteId, setPendingPlanDeleteId] = useState(null);

  // Day-editor dialog (theme + quick plan list for one day)
  const [dayEditorOpen, setDayEditorOpen] = useState(false);
  const [dayEditorDate, setDayEditorDate] = useState(null);
  const [dayThemeForm, setDayThemeForm] = useState({ name: '', color: 'violet', mode: 'recurring' });

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  // Rotate so that today's column comes first when the shown week contains today.
  const todayStr = fmt(new Date());
  const todayIdx = weekDays.findIndex((d) => fmt(d) === todayStr);
  const displayDays = todayIdx > 0
    ? [...weekDays.slice(todayIdx), ...weekDays.slice(0, todayIdx)]
    : weekDays;

  const fetchWeek = useCallback(async () => {
    try {
      const start = fmt(weekStart);
      const end = fmt(weekDays[6]);
      const [plansRes, ...dayResults] = await Promise.all([
        axios.get(`${API}/content/planner?start_date=${start}&end_date=${end}`, getAuthHeaders()),
        ...weekDays.map(d =>
          axios.get(`${API}/content/day?date=${fmt(d)}`, getAuthHeaders())
        ),
      ]);
      setPlans(plansRes.data || []);
      const map = {};
      dayResults.forEach((r) => {
        if (r.data && r.data.date) map[r.data.date] = r.data.theme;
      });
      setThemesByDate(map);
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders, weekStart]);

  useEffect(() => { fetchWeek(); }, [fetchWeek]);

  const shiftWeek = (dir) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d);
  };

  // ---- Day Editor (theme + plans for one day) ----
  const openDayEditor = (dateStr) => {
    const currentTheme = themesByDate[dateStr] || { name: '', color: 'violet', source: 'none' };
    setDayEditorDate(dateStr);
    setDayThemeForm({
      name: currentTheme.name || '',
      color: currentTheme.color || 'violet',
      mode: currentTheme.source === 'weekly' ? 'weekly' : 'recurring',
    });
    setDayEditorOpen(true);
  };

  const saveDayTheme = async () => {
    const mode = dayThemeForm.mode;
    try {
      await axios.put(`${API}/content/day/theme`, {
        date: dayEditorDate,
        name: dayThemeForm.name,
        color: dayThemeForm.color,
        mode,
      }, getAuthHeaders());
      toast.success(mode === 'recurring'
        ? 'Applied to every ' + new Date(dayEditorDate).toLocaleDateString(undefined, { weekday: 'long' })
        : 'Applied to this week only');
      fetchWeek();
    } catch (e) {
      toast.error('Failed to save theme');
    }
  };

  // ---- Plan CRUD ----
  const openNewPlan = (date) => {
    const dayTheme = themesByDate[date];
    setEditingPlan(null);
    setPlanForm({
      date, theme: dayTheme?.name || '', post_idea: '', platform: '', notes: '', status: 'Idea',
    });
    setPlanDialogOpen(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      date: plan.date, theme: plan.theme || '', post_idea: plan.post_idea || '',
      platform: plan.platform || '', notes: plan.notes || '', status: plan.status || 'Idea',
    });
    setPlanDialogOpen(true);
  };

  const ensurePlanId = async () => {
    if (editingPlan?.plan_id) return editingPlan.plan_id;
    if (!planForm.post_idea?.trim()) {
      toast.error('Add a post idea first, then attach media');
      return null;
    }
    try {
      const res = await axios.post(`${API}/content/planner`, planForm, getAuthHeaders());
      setEditingPlan(res.data);
      fetchWeek();
      return res.data.plan_id;
    } catch (e) {
      toast.error('Could not save the card');
      return null;
    }
  };

  const savePlan = async () => {
    try {
      if (editingPlan) {
        await axios.put(`${API}/content/planner/${editingPlan.plan_id}`, planForm, getAuthHeaders());
        toast.success(planForm.status === 'Posted' ? 'Marked Posted — moved to Archive' : 'Content updated');
      } else {
        await axios.post(`${API}/content/planner`, planForm, getAuthHeaders());
        toast.success(planForm.status === 'Posted' ? 'Added & archived as Posted' : 'Content added');
      }
      setPlanDialogOpen(false);
      fetchWeek();
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const deletePlan = async () => {
    if (!pendingPlanDeleteId) return;
    try {
      await axios.delete(`${API}/content/planner/${pendingPlanDeleteId}`, getAuthHeaders());
      toast.success('Deleted');
      fetchWeek();
    } catch (e) {
      toast.error('Failed to delete');
    } finally {
      setPendingPlanDeleteId(null);
    }
  };

  const editorDayName = dayEditorDate
    ? new Date(dayEditorDate).toLocaleDateString(undefined, { weekday: 'long' })
    : '';
  const editorDayPlans = plans.filter(p => p.date === dayEditorDate);

  return (
    <div className="space-y-3">
      {/* Week navigator */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)} data-testid="planner-prev-week">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="text-sm font-medium text-center">
          {weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} —{' '}
          {weekDays[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
        <Button variant="outline" size="sm" onClick={() => shiftWeek(1)} data-testid="planner-next-week">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => setWeekStart(startOfWeek(new Date()))}
        data-testid="planner-this-week"
      >
        Jump to This Week
      </Button>

      {/* Board */}
      <div
        className="grid grid-cols-2 gap-3"
        data-testid="planner-board"
      >
        {displayDays.map((d) => {
          const dateStr = fmt(d);
          const dayPlans = plans.filter(p => p.date === dateStr);
          const dayName = d.toLocaleDateString(undefined, { weekday: 'long' }).toLowerCase();
          const theme = themesByDate[dateStr];
          const showTheme = theme && theme.name;
          const isToday = dateStr === fmt(new Date());
          return (
            <div
              key={dateStr}
              className={`flex flex-col rounded-2xl bg-slate-50 dark:bg-slate-900/40 border ${isToday ? 'border-sky-400 ring-2 ring-sky-200 dark:ring-sky-900' : 'border-slate-200 dark:border-slate-800'}`}
              data-testid={`planner-day-${dayName}`}
            >
              {/* Column header */}
              <div className="p-2.5 pb-1.5">
                <div className="flex items-center justify-between gap-1">
                  <button
                    type="button"
                    onClick={() => openDayEditor(dateStr)}
                    className="flex items-baseline gap-1.5 text-left hover:opacity-80 transition-opacity min-w-0"
                    data-testid={`planner-day-header-${dayName}`}
                  >
                    <span className="text-sm font-outfit font-bold">
                      {d.toLocaleDateString(undefined, { weekday: 'short' })}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                    {isToday && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-sky-500 text-white">TODAY</span>
                    )}
                  </button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-sky-600 shrink-0 hover:bg-sky-100 dark:hover:bg-sky-900/40 rounded-full"
                    onClick={() => openNewPlan(dateStr)}
                    data-testid={`planner-add-${dayName}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => openDayEditor(dateStr)}
                  className="mt-1.5 w-full text-left"
                  data-testid={`planner-theme-pill-${dayName}`}
                >
                  {showTheme ? (
                    <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full border font-medium ${THEME_COLOR_CLASS[theme.color] || THEME_COLOR_CLASS.violet}`}>
                      {theme.name}
                      {theme.source === 'weekly' && <span className="ml-1 opacity-60">·wk</span>}
                    </span>
                  ) : (
                    <span className="text-[10px] text-sky-600/70 italic">+ Add theme</span>
                  )}
                </button>
              </div>

              {/* Cards */}
              <div className="px-2 pb-2.5 space-y-2 flex-1">
                {dayPlans.map(plan => (
                  <motion.div
                    key={plan.plan_id}
                    layout
                    onClick={() => openEditPlan(plan)}
                    className="group cursor-pointer p-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-sky-300 dark:hover:border-sky-700 transition-all"
                    data-testid={`planner-card-${plan.plan_id}`}
                  >
                    <p className="text-sm font-medium break-words line-clamp-3 mb-2">
                      {plan.post_idea || <span className="italic text-muted-foreground">Untitled idea</span>}
                    </p>
                    <div className="flex flex-wrap items-center gap-1">
                      {plan.platform && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold ${PLATFORM_COLORS[plan.platform] || 'bg-sky-100 text-sky-700'}`}>
                          {PLATFORM_SHORT[plan.platform] || plan.platform}
                        </span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-semibold ${STATUS_COLORS[plan.status] || STATUS_COLORS.Idea}`}>
                        {plan.status}
                      </span>
                    </div>
                    <div className="flex justify-end gap-0.5 mt-1.5 -mb-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <Button
                        size="icon" variant="ghost" className="h-6 w-6"
                        onClick={(e) => { e.stopPropagation(); openEditPlan(plan); }}
                        data-testid={`planner-edit-${plan.plan_id}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        size="icon" variant="ghost" className="h-6 w-6 text-red-500"
                        onClick={(e) => { e.stopPropagation(); setPendingPlanDeleteId(plan.plan_id); }}
                        data-testid={`planner-delete-${plan.plan_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
                <button
                  type="button"
                  onClick={() => openNewPlan(dateStr)}
                  className="w-full py-2 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-sky-400 hover:text-sky-500 transition-colors flex items-center justify-center gap-1 text-xs font-medium"
                  data-testid={`planner-add-card-${dayName}`}
                >
                  <Plus className="w-3.5 h-3.5" /> Add card
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Day Editor Dialog */}
      <Dialog open={dayEditorOpen} onOpenChange={setDayEditorOpen}>
        <DialogContent className="max-w-md" data-testid="day-editor-dialog">
          <DialogHeader>
            <DialogTitle>
              {dayEditorDate
                ? new Date(dayEditorDate).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
                : 'Edit day'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Theme name</Label>
              <Input
                value={dayThemeForm.name}
                onChange={(e) => setDayThemeForm({ ...dayThemeForm, name: e.target.value })}
                placeholder="e.g. Mindset Monday (leave empty to clear)"
                data-testid="day-theme-name-input"
              />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {THEME_COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setDayThemeForm({ ...dayThemeForm, color: c })}
                    className={`w-8 h-8 rounded-full border-2 ${THEME_COLOR_CLASS[c]} ${dayThemeForm.color === c ? 'ring-2 ring-offset-1 ring-slate-500' : ''}`}
                    data-testid={`day-theme-color-${c}`}
                  />
                ))}
              </div>
            </div>

            {/* Per-day recurring vs this-week toggle */}
            <div className="flex items-center justify-between gap-2 bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-900 rounded-xl p-3">
              <div className="flex items-center gap-2 min-w-0">
                {dayThemeForm.mode === 'recurring' ? (
                  <Repeat className="w-4 h-4 text-sky-600 shrink-0" />
                ) : (
                  <CalendarSolo className="w-4 h-4 text-sky-600 shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-sky-800 dark:text-sky-200">
                    {dayThemeForm.mode === 'recurring' ? `Every ${editorDayName}` : 'This week only'}
                  </p>
                  <p className="text-[11px] text-sky-700/70 dark:text-sky-300/70">
                    {dayThemeForm.mode === 'recurring'
                      ? `Repeats on every ${editorDayName}.`
                      : `Applies only to this ${editorDayName}.`}
                  </p>
                </div>
              </div>
              <Switch
                checked={dayThemeForm.mode === 'recurring'}
                onCheckedChange={(checked) => setDayThemeForm({ ...dayThemeForm, mode: checked ? 'recurring' : 'weekly' })}
                data-testid="day-theme-mode-toggle"
              />
            </div>

            <Button
              className="w-full bg-sky-600 hover:bg-sky-700"
              onClick={saveDayTheme}
              data-testid="day-theme-save-btn"
            >
              Save Theme
            </Button>

            {/* Quick plans list */}
            {editorDayPlans.length > 0 && (
              <div className="pt-2 border-t">
                <Label className="text-xs text-muted-foreground">
                  {editorDayPlans.length} planned post{editorDayPlans.length !== 1 ? 's' : ''}
                </Label>
                <div className="space-y-1.5 mt-2 max-h-48 overflow-y-auto">
                  {editorDayPlans.map(p => (
                    <div key={p.plan_id} className="flex items-center gap-2 p-2 rounded bg-slate-50 dark:bg-slate-800/60 text-sm">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${STATUS_COLORS[p.status] || STATUS_COLORS.Idea}`}>
                        {p.status}
                      </span>
                      <span className="flex-1 truncate">{p.post_idea || 'Untitled'}</span>
                      <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setDayEditorOpen(false); openEditPlan(p); }}>
                        <Edit2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button
              variant="outline"
              className="w-full border-sky-300 text-sky-600"
              onClick={() => { setDayEditorOpen(false); openNewPlan(dayEditorDate); }}
              data-testid="day-editor-add-post-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Post for This Day
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Plan Dialog */}
      <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Content' : 'Add Content'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Theme</Label>
              <Input
                value={planForm.theme}
                onChange={(e) => setPlanForm({ ...planForm, theme: e.target.value })}
                placeholder="e.g. Mindset Monday"
                data-testid="plan-theme-input"
              />
            </div>
            <div>
              <Label>Post Idea *</Label>
              <Textarea
                value={planForm.post_idea}
                onChange={(e) => setPlanForm({ ...planForm, post_idea: e.target.value })}
                placeholder="What's the post about?"
                rows={2}
                data-testid="plan-idea-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Platform</Label>
                <Select value={planForm.platform || undefined} onValueChange={(v) => setPlanForm({ ...planForm, platform: v })}>
                  <SelectTrigger data-testid="plan-platform-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={planForm.status} onValueChange={(v) => setPlanForm({ ...planForm, status: v })}>
                  <SelectTrigger data-testid="plan-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={planForm.notes}
                onChange={(e) => setPlanForm({ ...planForm, notes: e.target.value })}
                placeholder="Hooks, hashtags, references..."
                rows={3}
                data-testid="plan-notes-input"
              />
            </div>
            <div className="pt-1 border-t">
              <MediaManager planId={editingPlan?.plan_id || null} ensurePlanId={ensurePlanId} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanDialogOpen(false)}>Cancel</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={savePlan} data-testid="plan-save-btn">
              {editingPlan ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingPlanDeleteId}
        onOpenChange={(o) => !o && setPendingPlanDeleteId(null)}
        onConfirm={deletePlan}
        title="Delete this content plan?"
        description="This will permanently remove the planned post from your calendar."
        testId="planner-confirm-delete"
      />
    </div>
  );
};

export default PlannerPanel;
